'use client';

import { type Outage } from '@/lib/outages-db';
import { formatDistanceToNow } from 'date-fns';
import { useTheme } from '@/components/ThemeProvider';

interface OutageCardProps {
  outage: Outage;
  onClick: () => void;
}

const severityColors = {
  critical: 'bg-red-500/10 border-red-500 text-red-400',
  major: 'bg-accent-500/10 border-accent text-accent',
  moderate: 'bg-yellow-500/10 border-yellow-500 text-yellow-400',
};

const statusColors = {
  ongoing: 'bg-red-500/10 text-red-400',
  investigating: 'bg-yellow-500/10 text-yellow-400',
  resolved: 'bg-green-500/10 text-green-400',
};

export function OutageCard({ outage, onClick }: OutageCardProps) {
  const { theme } = useTheme();
  const duration = outage.ended_at
    ? Math.round((new Date(outage.ended_at).getTime() - new Date(outage.started_at).getTime()) / (1000 * 60 * 60))
    : null;

  return (
    <div
      onClick={onClick}
      className="group cursor-pointer border transition-all rounded-lg p-6 hover:border-border-muted0"
      style={{
        backgroundColor: theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : 'white',
        borderColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(10, 30, 46, 0.7)' : '#f9fafb';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = theme === 'dark' ? 'rgba(10, 30, 46, 0.5)' : 'white';
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-accent font-semibold text-lg">{outage.vendor}</span>
            <span
              className={`px-2 py-0.5 text-xs rounded border ${severityColors[outage.severity]}`}
            >
              {outage.severity}
            </span>
            <span className={`px-2 py-0.5 text-xs rounded ${statusColors[outage.status]}`}>
              {outage.status}
            </span>
          </div>
          <h3 style={{ color: theme === 'dark' ? '#E8D5B7' : '#111827' }} className="text-xl font-semibold group-hover:text-accent transition-colors">
            {outage.title}
          </h3>
        </div>
      </div>

      {/* Description */}
      <p style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.7)' : '#1f2937' }} className="text-sm mb-4 line-clamp-2">{outage.description}</p>

      {/* Metadata */}
      <div style={{ color: theme === 'dark' ? 'rgba(232, 213, 183, 0.6)' : '#374151' }} className="flex flex-wrap gap-4 text-xs">
        <div>
          <span className="font-medium">Started:</span>{' '}
          {new Date(outage.started_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
        {duration !== null && (
          <div>
            <span className="font-medium">Duration:</span> {duration}h
          </div>
        )}
        {outage.impact_scope && (
          <div>
            <span className="font-medium">Scope:</span> {outage.impact_scope}
          </div>
        )}
        {outage.estimated_affected_customers && (
          <div>
            <span className="font-medium">Affected:</span>{' '}
            {Number(outage.estimated_affected_customers).toLocaleString()} customers
          </div>
        )}
      </div>

      {/* Tags */}
      {outage.tags && outage.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {outage.tags.slice(0, 4).map((tag) => (
            <span
              key={tag}
              style={{
                backgroundColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.05)' : '#f3f4f6',
                color: theme === 'dark' ? 'rgba(232, 213, 183, 0.5)' : '#374151',
                borderColor: theme === 'dark' ? 'rgba(232, 213, 183, 0.1)' : '#e5e7eb'
              }}
              className="px-2 py-1 text-xs rounded border"
            >
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
